import React, { useState, useMemo } from "react";
import Page from "../components/Page";
import { useTips, useStaffOptions, payoutTips, allocatePoolTip } from "../controllers/tips.controller";
import { useStoreSettings } from "../controllers/settings.controller";
import {
  IconCoins,
  IconUser,
  IconUsersGroup,
  IconClock,
  IconCheck,
  IconRefresh,
  IconSearch,
  IconFilter,
  IconX,
  IconCash,
  IconBuildingBank,
  IconPlus,
  IconTrash,
  IconAlertCircle,
  IconReceipt,
  IconArrowUpRight
} from "@tabler/icons-react";
import { toast } from "react-hot-toast";

export default function TipManagementPage() {
  const { data: storeSettings } = useStoreSettings();
  const currency = storeSettings?.currency || "Rs.";

  // Filters state
  const [filters, setFilters] = useState({
    from: "",
    to: "",
    status: "all",
    distribution_type: "all",
    employee_id: "",
    search: "",
  });

  const { tips, summary, isLoading, mutate } = useTips(filters);
  const { staff } = useStaffOptions();

  // Selection state for batch payout
  const [selectedTipIds, setSelectedTipIds] = useState(new Set());

  // Modal states
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
  const [payoutMethod, setPayoutMethod] = useState("Cash");
  const [payoutNotes, setPayoutNotes] = useState("");
  const [isPayoutSubmitting, setIsPayoutSubmitting] = useState(false);

  // Pool allocation modal state
  const [poolModalTip, setPoolModalTip] = useState(null);
  const [poolAllocations, setPoolAllocations] = useState([]);
  const [isAllocating, setIsAllocating] = useState(false);

  const formatCurrency = (amount) => {
    return `${currency} ${Number(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Filter change helper
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      from: "",
      to: "",
      status: "all",
      distribution_type: "all",
      employee_id: "",
      search: "",
    });
  };

  // Payable tips logic (can be paid out)
  const isTipPayable = (tip) => {
    return (
      (tip.status === "collected" && tip.staff_user_id) ||
      tip.status === "allocated"
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const payableIds = tips.filter(isTipPayable).map((t) => t.id);
      setSelectedTipIds(new Set(payableIds));
    } else {
      setSelectedTipIds(new Set());
    }
  };

  const handleToggleSelectTip = (id) => {
    setSelectedTipIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Open payout modal for selected tips or single tip
  const handleOpenPayout = (singleTipId = null) => {
    if (singleTipId) {
      setSelectedTipIds(new Set([singleTipId]));
    }
    if (!singleTipId && selectedTipIds.size === 0) {
      toast.error("Please select at least one tip to pay out.");
      return;
    }
    setPayoutMethod("Cash");
    setPayoutNotes("");
    setIsPayoutModalOpen(true);
  };

  const selectedTipsList = useMemo(() => {
    return tips.filter((t) => selectedTipIds.has(t.id));
  }, [tips, selectedTipIds]);

  const selectedTotalAmount = useMemo(() => {
    return selectedTipsList.reduce((sum, t) => sum + Number(t.tip_amount || 0), 0);
  }, [selectedTipsList]);

  const handleConfirmPayout = async () => {
    if (selectedTipIds.size === 0) return;
    setIsPayoutSubmitting(true);
    try {
      const ids = Array.from(selectedTipIds);
      const res = await payoutTips(ids, payoutMethod, payoutNotes);
      toast.success(res?.message || "Tips successfully paid out to staff!");
      setIsPayoutModalOpen(false);
      setSelectedTipIds(new Set());
      mutate();
    } catch (err) {
      console.error("Payout error:", err);
      toast.error(err?.response?.data?.message || "Failed to process tip payout.");
    } finally {
      setIsPayoutSubmitting(false);
    }
  };

  // Open pool allocation modal
  const handleOpenPoolAllocation = (tip) => {
    setPoolModalTip(tip);
    // Initialize with 2 staff rows if staff exists
    if (staff && staff.length > 0) {
      setPoolAllocations([
        { staff_user_id: staff[0]?.id, amount: "" },
        { staff_user_id: staff[1]?.id || staff[0]?.id, amount: "" },
      ]);
    } else {
      setPoolAllocations([{ staff_user_id: "", amount: "" }]);
    }
  };

  const handleAddAllocationRow = () => {
    const firstStaffId = staff?.[0]?.id || "";
    setPoolAllocations((prev) => [...prev, { staff_user_id: firstStaffId, amount: "" }]);
  };

  const handleRemoveAllocationRow = (index) => {
    setPoolAllocations((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAllocationChange = (index, field, value) => {
    setPoolAllocations((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSplitEqually = () => {
    if (!poolModalTip || poolAllocations.length === 0) return;
    const count = poolAllocations.length;
    const total = Number(poolModalTip.tip_amount || 0);
    const equalShare = (total / count).toFixed(2);
    // Adjust remainder on first element
    let remainder = Number((total - equalShare * count).toFixed(2));

    setPoolAllocations((prev) =>
      prev.map((row, idx) => {
        let amt = Number(equalShare);
        if (idx === 0) amt += remainder;
        return { ...row, amount: amt.toFixed(2) };
      })
    );
  };

  const totalAllocatedAmount = useMemo(() => {
    return poolAllocations.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  }, [poolAllocations]);

  const remainingPoolAmount = useMemo(() => {
    const total = Number(poolModalTip?.tip_amount || 0);
    return Number((total - totalAllocatedAmount).toFixed(2));
  }, [poolModalTip, totalAllocatedAmount]);

  const handleConfirmAllocation = async () => {
    if (!poolModalTip) return;
    const tipTotal = Number(poolModalTip.tip_amount || 0);

    if (Math.abs(remainingPoolAmount) > 0.01) {
      toast.error(`Allocated total (${formatCurrency(totalAllocatedAmount)}) must equal tip amount (${formatCurrency(tipTotal)})`);
      return;
    }

    for (const alloc of poolAllocations) {
      if (!alloc.staff_user_id) {
        toast.error("Please select a staff member for each allocation.");
        return;
      }
      if (Number(alloc.amount || 0) <= 0) {
        toast.error("Each allocation amount must be greater than zero.");
        return;
      }
    }

    setIsAllocating(true);
    try {
      const res = await allocatePoolTip(poolModalTip.id, poolAllocations);
      toast.success(res?.message || "Pooled tip successfully allocated!");
      setPoolModalTip(null);
      mutate();
    } catch (err) {
      console.error("Pool allocation error:", err);
      toast.error(err?.response?.data?.message || "Failed to allocate pooled tip.");
    } finally {
      setIsAllocating(false);
    }
  };

  return (
    <Page className="p-4 md:p-6 space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-base-content flex items-center gap-2">
            <IconCoins className="w-7 h-7 text-primary" />
            Tip Management & Staff Payouts
          </h1>
          <p className="text-sm text-base-content/70 mt-1">
            Track collected guest tips, distribute pooled gratuities, and manage staff payouts with double-entry accounting.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => mutate()}
            disabled={isLoading}
            className="btn btn-outline btn-sm gap-2"
            aria-label="Refresh Tips"
          >
            <IconRefresh className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </button>

          <button
            onClick={() => handleOpenPayout()}
            disabled={selectedTipIds.size === 0}
            className="btn btn-primary btn-sm gap-2 shadow-sm"
          >
            <IconCheck className="w-4 h-4" />
            Pay Selected Tips ({selectedTipIds.size})
          </button>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-base-100 p-4 rounded-xl border border-base-200 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-base-content/60 uppercase tracking-wider">
              Total Collected
            </span>
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              <IconCoins className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-xl font-bold text-base-content">
              {formatCurrency(summary.total_collected)}
            </div>
            <div className="text-xs text-base-content/50 mt-0.5">
              Across all invoices
            </div>
          </div>
        </div>

        <div className="bg-base-100 p-4 rounded-xl border border-base-200 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-base-content/60 uppercase tracking-wider">
              Direct Staff Tips
            </span>
            <div className="p-2 rounded-lg bg-blue-500/10 text-blue-500">
              <IconUser className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-xl font-bold text-base-content">
              {formatCurrency(summary.direct_tips)}
            </div>
            <div className="text-xs text-base-content/50 mt-0.5">
              Assigned directly
            </div>
          </div>
        </div>

        <div className="bg-base-100 p-4 rounded-xl border border-base-200 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-base-content/60 uppercase tracking-wider">
              Shift / Team Pools
            </span>
            <div className="p-2 rounded-lg bg-purple-500/10 text-purple-500">
              <IconUsersGroup className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-xl font-bold text-base-content">
              {formatCurrency(summary.pooled_tips)}
            </div>
            <div className="text-xs text-base-content/50 mt-0.5">
              Shared team tips
            </div>
          </div>
        </div>

        <div className="bg-base-100 p-4 rounded-xl border border-base-200 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-base-content/60 uppercase tracking-wider">
              Paid Out to Staff
            </span>
            <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-500">
              <IconCheck className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-xl font-bold text-base-content">
              {formatCurrency(summary.paid_tips)}
            </div>
            <div className="text-xs text-base-content/50 mt-0.5">
              Settled with employees
            </div>
          </div>
        </div>

        <div className="bg-base-100 p-4 rounded-xl border border-base-200 shadow-sm flex flex-col justify-between col-span-2 md:col-span-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-base-content/60 uppercase tracking-wider">
              Pending Payout
            </span>
            <div className="p-2 rounded-lg bg-amber-500/10 text-amber-500">
              <IconClock className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-xl font-bold text-amber-600 dark:text-amber-400">
              {formatCurrency(summary.pending_payout)}
            </div>
            <div className="text-xs text-base-content/50 mt-0.5">
              Liability in Tips Payable
            </div>
          </div>
        </div>
      </div>

      {/* Filters Toolbar */}
      <div className="bg-base-100 p-4 rounded-xl border border-base-200 shadow-sm space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold flex items-center gap-2 text-base-content">
            <IconFilter className="w-4 h-4 text-primary" />
            Filter Tips & Transactions
          </div>
          {(filters.from || filters.to || filters.status !== "all" || filters.distribution_type !== "all" || filters.employee_id || filters.search) && (
            <button
              onClick={handleResetFilters}
              className="btn btn-ghost btn-xs text-error gap-1"
            >
              <IconX className="w-3.5 h-3.5" />
              Reset Filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
          {/* Search */}
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-base-content/40">
              <IconSearch className="w-4 h-4" />
            </span>
            <input
              type="text"
              placeholder="Search invoice, staff, customer..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="input input-sm input-bordered w-full pl-9 text-xs"
            />
          </div>

          {/* Status */}
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
            className="select select-sm select-bordered text-xs"
          >
            <option value="all">All Statuses</option>
            <option value="collected">Collected (Pending)</option>
            <option value="allocated">Allocated (Ready to Pay)</option>
            <option value="paid">Paid Out</option>
            <option value="voided">Voided / Reversed</option>
          </select>

          {/* Distribution Type */}
          <select
            value={filters.distribution_type}
            onChange={(e) => handleFilterChange("distribution_type", e.target.value)}
            className="select select-sm select-bordered text-xs"
          >
            <option value="all">All Distribution Types</option>
            <option value="direct">Direct Staff Tip</option>
            <option value="pool">Shift / Team Pool</option>
            <option value="pending">Pending Allocation</option>
          </select>

          {/* Staff Filter */}
          <select
            value={filters.employee_id}
            onChange={(e) => handleFilterChange("employee_id", e.target.value)}
            className="select select-sm select-bordered text-xs"
          >
            <option value="">All Staff Members</option>
            {staff.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.role || "Staff"})
              </option>
            ))}
          </select>

          {/* Date Range */}
          <div className="flex items-center gap-1">
            <input
              type="date"
              value={filters.from}
              onChange={(e) => handleFilterChange("from", e.target.value)}
              className="input input-sm input-bordered w-full text-xs"
              aria-label="From Date"
            />
            <span className="text-xs text-base-content/40">to</span>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => handleFilterChange("to", e.target.value)}
              className="input input-sm input-bordered w-full text-xs"
              aria-label="To Date"
            />
          </div>
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-base-100 rounded-xl border border-base-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table table-sm w-full text-left">
            <thead className="bg-base-200/50 text-base-content/70 text-xs uppercase tracking-wider font-semibold border-b border-base-200">
              <tr>
                <th className="w-10 text-center">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-xs checkbox-primary"
                    onChange={handleSelectAll}
                    checked={
                      tips.length > 0 &&
                      tips.filter(isTipPayable).length > 0 &&
                      tips.filter(isTipPayable).every((t) => selectedTipIds.has(t.id))
                    }
                  />
                </th>
                <th>Date & Time</th>
                <th>Invoice / Ref</th>
                <th>Source</th>
                <th>Distribution</th>
                <th>Recipient Staff</th>
                <th className="text-right">Tip Amount</th>
                <th className="text-center">Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-base-200 text-xs">
              {isLoading ? (
                <tr>
                  <td colSpan="9" className="text-center py-12">
                    <div className="loading loading-spinner text-primary"></div>
                    <div className="text-xs text-base-content/60 mt-2">Loading tips data...</div>
                  </td>
                </tr>
              ) : tips.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-12 text-base-content/50">
                    <IconCoins className="w-10 h-10 mx-auto opacity-30 mb-2" />
                    <p className="font-medium">No tip records found</p>
                    <p className="text-xs mt-1">
                      Tips collected during POS, Order settlement or Room checkout will appear here.
                    </p>
                  </td>
                </tr>
              ) : (
                tips.map((tip) => {
                  const payable = isTipPayable(tip);
                  const isChecked = selectedTipIds.has(tip.id);
                  const dateStr = tip.created_at
                    ? new Date(tip.created_at).toLocaleString("en-US", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })
                    : "—";

                  return (
                    <tr
                      key={tip.id}
                      className={`hover:bg-base-200/40 transition-colors ${
                        isChecked ? "bg-primary/5" : ""
                      }`}
                    >
                      {/* Checkbox */}
                      <td className="text-center">
                        {payable ? (
                          <input
                            type="checkbox"
                            className="checkbox checkbox-xs checkbox-primary"
                            checked={isChecked}
                            onChange={() => handleToggleSelectTip(tip.id)}
                          />
                        ) : (
                          <span className="text-base-content/20 text-xs">—</span>
                        )}
                      </td>

                      {/* Date & Time */}
                      <td className="whitespace-nowrap font-medium text-base-content">
                        {dateStr}
                      </td>

                      {/* Invoice / Ref */}
                      <td>
                        <div className="font-semibold text-primary flex items-center gap-1">
                          <IconReceipt className="w-3.5 h-3.5 text-base-content/40" />
                          #{tip.invoice_number || `INV-${tip.invoice_id}`}
                        </div>
                        {tip.customer_name && (
                          <div className="text-[11px] text-base-content/60">
                            {tip.customer_name}
                          </div>
                        )}
                      </td>

                      {/* Source */}
                      <td>
                        <span className="badge badge-ghost badge-sm text-[11px]">
                          {tip.room_number
                            ? `Room ${tip.room_number}`
                            : tip.table_title
                            ? `Table ${tip.table_title}`
                            : tip.order_id
                            ? `Order #${tip.order_id}`
                            : "Direct POS"}
                        </span>
                      </td>

                      {/* Distribution Type */}
                      <td>
                        {tip.distribution_type === "direct" && (
                          <span className="badge badge-info badge-outline badge-sm gap-1 text-[11px]">
                            <IconUser className="w-3 h-3" />
                            Direct Staff
                          </span>
                        )}
                        {tip.distribution_type === "pool" && (
                          <span className="badge badge-secondary badge-outline badge-sm gap-1 text-[11px]">
                            <IconUsersGroup className="w-3 h-3" />
                            Team Pool
                          </span>
                        )}
                        {tip.distribution_type === "pending" && (
                          <span className="badge badge-warning badge-outline badge-sm gap-1 text-[11px]">
                            <IconClock className="w-3 h-3" />
                            Pending Alloc
                          </span>
                        )}
                      </td>

                      {/* Recipient Staff */}
                      <td>
                        {tip.staff_name ? (
                          <div className="flex items-center gap-1.5 font-medium text-base-content">
                            <div className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold">
                              {tip.staff_name.charAt(0).toUpperCase()}
                            </div>
                            <span>{tip.staff_name}</span>
                          </div>
                        ) : tip.distribution_type === "pool" ? (
                          <span className="text-base-content/50 italic text-[11px]">
                            Shift Pool / Shared
                          </span>
                        ) : (
                          <span className="text-base-content/40 italic text-[11px]">
                            Unassigned
                          </span>
                        )}
                      </td>

                      {/* Tip Amount */}
                      <td className="text-right font-bold text-base-content whitespace-nowrap">
                        {formatCurrency(tip.tip_amount)}
                      </td>

                      {/* Status */}
                      <td className="text-center">
                        {tip.status === "collected" && (
                          <span className="badge badge-sm badge-warning badge-outline font-medium text-[11px]">
                            Collected
                          </span>
                        )}
                        {tip.status === "allocated" && (
                          <span className="badge badge-sm badge-info font-medium text-[11px]">
                            Allocated
                          </span>
                        )}
                        {tip.status === "paid" && (
                          <span className="badge badge-sm badge-success font-medium text-[11px] gap-1">
                            <IconCheck className="w-3 h-3" />
                            Paid
                          </span>
                        )}
                        {tip.status === "voided" && (
                          <span className="badge badge-sm badge-error badge-outline font-medium text-[11px]">
                            Voided
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1">
                          {/* Pool allocation action */}
                          {tip.distribution_type === "pool" && tip.status === "collected" && (
                            <button
                              onClick={() => handleOpenPoolAllocation(tip)}
                              className="btn btn-xs btn-secondary gap-1"
                              aria-label="Allocate Pooled Tip among team members"
                            >
                              <IconUsersGroup className="w-3 h-3" />
                              Allocate Pool
                            </button>
                          )}

                          {/* Individual Payout action */}
                          {payable && (
                            <button
                              onClick={() => handleOpenPayout(tip.id)}
                              className="btn btn-xs btn-primary gap-1"
                              aria-label="Pay out this tip"
                            >
                              <IconCash className="w-3 h-3" />
                              Pay Out
                            </button>
                          )}

                          {/* Paid info */}
                          {tip.status === "paid" && (
                            <span className="text-[11px] text-base-content/50">
                              {tip.payout_method || "Paid"}
                            </span>
                          )}

                          {/* Voided info */}
                          {tip.status === "voided" && (
                            <span className="text-[11px] text-error/70 italic">
                              Reversed
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payout Modal */}
      {isPayoutModalOpen && (
        <div className="modal modal-open">
          <div className="modal-box max-w-lg">
            <h3 className="font-bold text-lg flex items-center gap-2 text-base-content">
              <IconCash className="w-6 h-6 text-primary" />
              Process Staff Tip Payout
            </h3>
            <p className="text-xs text-base-content/70 mt-1">
              Confirm recording payout for the selected tips. This posts a debit to <b>Tips Payable</b> and a credit to <b>{payoutMethod === "Cash" ? "Cash / Drawer" : "Bank"}</b>.
            </p>

            <div className="bg-base-200/60 p-3 rounded-lg my-4 space-y-2 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-base-content/70">Selected Tips Count:</span>
                <span className="font-semibold text-base-content">{selectedTipsList.length}</span>
              </div>
              <div className="flex justify-between items-center text-sm font-bold border-t border-base-300 pt-2">
                <span>Total Payout Amount:</span>
                <span className="text-primary text-base">{formatCurrency(selectedTotalAmount)}</span>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="label text-xs font-semibold py-1">
                  Payout Payment Method
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setPayoutMethod("Cash")}
                    className={`btn btn-sm text-xs justify-start gap-2 ${
                      payoutMethod === "Cash" ? "btn-primary" : "btn-outline"
                    }`}
                  >
                    <IconCash className="w-4 h-4" />
                    Cash (from Drawer)
                  </button>
                  <button
                    type="button"
                    onClick={() => setPayoutMethod("Bank Transfer")}
                    className={`btn btn-sm text-xs justify-start gap-2 ${
                      payoutMethod === "Bank Transfer" ? "btn-primary" : "btn-outline"
                    }`}
                  >
                    <IconBuildingBank className="w-4 h-4" />
                    Bank / Digital Transfer
                  </button>
                </div>
              </div>

              <div>
                <label className="label text-xs font-semibold py-1">
                  Payout Reference & Notes (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. End of shift distribution, voucher #..."
                  value={payoutNotes}
                  onChange={(e) => setPayoutNotes(e.target.value)}
                  className="input input-sm input-bordered w-full text-xs"
                />
              </div>
            </div>

            <div className="modal-action mt-6">
              <button
                type="button"
                onClick={() => setIsPayoutModalOpen(false)}
                disabled={isPayoutSubmitting}
                className="btn btn-sm btn-ghost"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmPayout}
                disabled={isPayoutSubmitting}
                className="btn btn-sm btn-primary gap-2"
              >
                {isPayoutSubmitting ? (
                  <span className="loading loading-spinner loading-xs"></span>
                ) : (
                  <IconCheck className="w-4 h-4" />
                )}
                Confirm Payout ({formatCurrency(selectedTotalAmount)})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pool Allocation Modal */}
      {poolModalTip && (
        <div className="modal modal-open">
          <div className="modal-box max-w-xl">
            <h3 className="font-bold text-lg flex items-center gap-2 text-base-content">
              <IconUsersGroup className="w-6 h-6 text-secondary" />
              Allocate Pooled Tip
            </h3>
            <p className="text-xs text-base-content/70 mt-1">
              Split this shared tip among the serving staff. The sum of distributed shares must exactly match the collected tip amount.
            </p>

            <div className="bg-base-200/60 p-3 rounded-lg my-4 flex items-center justify-between text-xs">
              <div>
                <div className="text-base-content/60">Invoice Ref:</div>
                <div className="font-semibold text-primary">#{poolModalTip.invoice_number || poolModalTip.invoice_id}</div>
              </div>
              <div>
                <div className="text-base-content/60">Total Pooled Tip:</div>
                <div className="font-bold text-base text-base-content">{formatCurrency(poolModalTip.tip_amount)}</div>
              </div>
              <div>
                <div className="text-base-content/60">Remaining to Allocate:</div>
                <div
                  className={`font-bold text-base ${
                    Math.abs(remainingPoolAmount) < 0.01
                      ? "text-success"
                      : "text-error"
                  }`}
                >
                  {formatCurrency(remainingPoolAmount)}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-semibold text-base-content">Staff Allocations</span>
              <button
                type="button"
                onClick={handleSplitEqually}
                className="btn btn-ghost btn-xs text-secondary gap-1"
                aria-label="Divide equally among added staff members"
              >
                <IconRefresh className="w-3.5 h-3.5" />
                Split Equally
              </button>
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {poolAllocations.map((row, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-base-200/30 p-2 rounded-lg">
                  <select
                    value={row.staff_user_id}
                    onChange={(e) => handleAllocationChange(idx, "staff_user_id", e.target.value)}
                    className="select select-sm select-bordered flex-1 text-xs"
                  >
                    <option value="">Select Staff Member</option>
                    {staff.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.role || "Staff"})
                      </option>
                    ))}
                  </select>

                  <div className="w-32 relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none text-xs text-base-content/40">
                      {currency}
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={row.amount}
                      onChange={(e) => handleAllocationChange(idx, "amount", e.target.value)}
                      className="input input-sm input-bordered w-full pl-8 text-xs font-semibold text-right"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemoveAllocationRow(idx)}
                    disabled={poolAllocations.length <= 1}
                    className="btn btn-ghost btn-xs text-error btn-square"
                  >
                    <IconTrash className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-3">
              <button
                type="button"
                onClick={handleAddAllocationRow}
                className="btn btn-outline btn-xs gap-1"
              >
                <IconPlus className="w-3.5 h-3.5" />
                Add Staff Member
              </button>
            </div>

            <div className="modal-action mt-6">
              <button
                type="button"
                onClick={() => setPoolModalTip(null)}
                disabled={isAllocating}
                className="btn btn-sm btn-ghost"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmAllocation}
                disabled={isAllocating || Math.abs(remainingPoolAmount) > 0.01}
                className="btn btn-sm btn-secondary gap-2"
              >
                {isAllocating ? (
                  <span className="loading loading-spinner loading-xs"></span>
                ) : (
                  <IconCheck className="w-4 h-4" />
                )}
                Save Allocation
              </button>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
